import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, MagnifyingGlass, Eye, PencilSimple, Trash, Article, Clock,
  Copy, CheckSquare, Square, CaretDown, ClockCounterClockwise
} from '@phosphor-icons/react';
import PageEditorModal from './PageEditorModal';
import VersionHistoryModal from './VersionHistoryModal';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['information', 'legal', 'writer', 'reader', 'other'];

export default function CmsPages() {
  const [pages, setPages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [selectedPages, setSelectedPages] = useState([]);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistoryPageId, setVersionHistoryPageId] = useState(null);

  useEffect(() => {
    fetchPages();
    fetchStats();
  }, [statusFilter, categoryFilter, search]);

  const fetchPages = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (search) params.search = search;

      const res = await axios.get(`${API}/api/admin/pages`, {
        params,
        withCredentials: true,
      });
      setPages(res.data.pages || []);
    } catch (error) {
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/pages/stats/overview`, {
        withCredentials: true,
      });
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const handleSave = async (formData) => {
    try {
      if (editingPage) {
        await axios.patch(
          `${API}/api/admin/pages/${editingPage.page_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Page updated successfully');
      } else {
        await axios.post(`${API}/api/admin/pages`, formData, {
          withCredentials: true,
        });
        toast.success('Page created successfully');
      }
      setShowEditor(false);
      setEditingPage(null);
      fetchPages();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save page');
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setShowEditor(true);
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;

    try {
      await axios.delete(`${API}/api/admin/pages/${pageId}`, {
        withCredentials: true,
      });
      toast.success('Page deleted');
      fetchPages();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete page');
    }
  };

  const handleDuplicate = async (pageId) => {
    try {
      const res = await axios.post(
        `${API}/api/admin/pages/${pageId}/duplicate`,
        {},
        { withCredentials: true }
      );
      toast.success('Page duplicated successfully');
      fetchPages();
      fetchStats();
    } catch (error) {
      toast.error('Failed to duplicate page');
    }
  };

  const toggleSelectPage = (pageId) => {
    setSelectedPages(prev =>
      prev.includes(pageId)
        ? prev.filter(id => id !== pageId)
        : [...prev, pageId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedPages.length === pages.length) {
      setSelectedPages([]);
    } else {
      setSelectedPages(pages.map(p => p.page_id));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedPages.length === 0) {
      toast.error('No pages selected');
      return;
    }

    const actionLabels = {
      publish: 'publish',
      unpublish: 'unpublish',
      archive: 'archive',
      delete: 'delete',
    };

    if (action === 'delete' && !window.confirm(`Are you sure you want to delete ${selectedPages.length} page(s)?`)) {
      return;
    }

    try {
      await axios.post(
        `${API}/api/admin/pages/bulk-action?action=${action}`,
        { page_ids: selectedPages },
        { withCredentials: true }
      );
      toast.success(`Successfully ${actionLabels[action]}ed ${selectedPages.length} page(s)`);
      setSelectedPages([]);
      setShowBulkMenu(false);
      fetchPages();
      fetchStats();
    } catch (error) {
      toast.error(`Failed to ${actionLabels[action]} pages`);
    }
  };

  const handleNewPage = () => {
    setEditingPage(null);
    setShowEditor(true);
  };

  const handleShowVersionHistory = (pageId) => {
    setVersionHistoryPageId(pageId);
    setShowVersionHistory(true);
  };

  const handleVersionRestore = () => {
    setShowVersionHistory(false);
    fetchPages();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-24 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Pages Management
          </h1>
          <p className="text-sm text-muted-foreground">
            Create and manage static pages, legal documents, and information pages
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedPages.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowBulkMenu(!showBulkMenu)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-lg font-semibold hover:bg-muted"
              >
                Bulk Actions ({selectedPages.length})
                <CaretDown size={16} />
              </button>
              {showBulkMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-background border border-border rounded-lg shadow-lg z-10">
                  <button
                    onClick={() => handleBulkAction('publish')}
                    className="w-full text-left px-4 py-2 hover:bg-muted text-sm"
                  >
                    Publish
                  </button>
                  <button
                    onClick={() => handleBulkAction('unpublish')}
                    className="w-full text-left px-4 py-2 hover:bg-muted text-sm"
                  >
                    Unpublish
                  </button>
                  <button
                    onClick={() => handleBulkAction('archive')}
                    className="w-full text-left px-4 py-2 hover:bg-muted text-sm"
                  >
                    Archive
                  </button>
                  <button
                    onClick={() => handleBulkAction('delete')}
                    className="w-full text-left px-4 py-2 hover:bg-muted text-sm text-red-600"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
          <button
            onClick={handleNewPage}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90"
          >
            <Plus size={20} weight="bold" />
            New Page
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-6 gap-4 mb-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase text-muted-foreground mb-1">Total Pages</p>
            <p className="text-2xl font-bold">{stats.total_pages}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase text-muted-foreground mb-1">Published</p>
            <p className="text-2xl font-bold text-green-600">{stats.published}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase text-muted-foreground mb-1">Draft</p>
            <p className="text-2xl font-bold text-amber-600">{stats.draft}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase text-muted-foreground mb-1">Scheduled</p>
            <p className="text-2xl font-bold text-blue-600">{stats.scheduled}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase text-muted-foreground mb-1">Pending</p>
            <p className="text-2xl font-bold text-purple-600">{stats.pending_review || 0}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase text-muted-foreground mb-1">Archived</p>
            <p className="text-2xl font-bold text-gray-600">{stats.archived || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6">
        <div className="grid grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search pages..."
              className="w-full pl-9 pr-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm"
          >
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="pending_review">Pending Review</option>
            <option value="scheduled">Scheduled</option>
            <option value="archived">Archived</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </option>
            ))}
          </select>
          <select
            value={visibilityFilter}
            onChange={(e) => setVisibilityFilter(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm"
          >
            <option value="">All Visibility</option>
            <option value="public">Public</option>
            <option value="private">Private</option>
            <option value="password_protected">Password Protected</option>
            <option value="members_only">Members Only</option>
          </select>
        </div>
      </div>

      {/* Pages Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 w-12">
                <button onClick={toggleSelectAll} className="p-1">
                  {selectedPages.length === pages.length ? (
                    <CheckSquare size={20} weight="fill" className="text-primary" />
                  ) : (
                    <Square size={20} />
                  )}
                </button>
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase">Title</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase">Slug</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase">Category</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase">Views</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase">Updated</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pages.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-12 text-muted-foreground">
                  <Article size={48} weight="duotone" className="mx-auto mb-2 opacity-50" />
                  <p>No pages found</p>
                </td>
              </tr>
            ) : (
              pages.map((page) => (
                <tr key={page.page_id} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <button onClick={() => toggleSelectPage(page.page_id)} className="p-1">
                      {selectedPages.includes(page.page_id) ? (
                        <CheckSquare size={20} weight="fill" className="text-primary" />
                      ) : (
                        <Square size={20} />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold">{page.title}</p>
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-xs bg-muted px-2 py-1 rounded">{page.slug}</code>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs bg-muted px-2 py-1 rounded capitalize">{page.category}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs px-2 py-1 rounded font-medium ${
                        page.status === 'published'
                          ? 'bg-green-100 text-green-700'
                          : page.status === 'draft'
                          ? 'bg-amber-100 text-amber-700'
                          : page.status === 'pending_review'
                          ? 'bg-purple-100 text-purple-700'
                          : page.status === 'archived'
                          ? 'bg-gray-100 text-gray-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {page.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Eye size={16} className="text-muted-foreground" />
                      <span className="text-sm font-medium">{page.total_views || 0}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {new Date(page.updated_at).toLocaleDateString('en-IN', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <a
                        href={`/page/${page.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 hover:bg-muted rounded"
                        title="View"
                      >
                        <Eye size={18} />
                      </a>
                      <button
                        onClick={() => handleEdit(page)}
                        className="p-1.5 hover:bg-muted rounded"
                        title="Edit"
                      >
                        <PencilSimple size={18} />
                      </button>
                      <button
                        onClick={() => handleDuplicate(page.page_id)}
                        className="p-1.5 hover:bg-muted rounded"
                        title="Duplicate"
                      >
                        <Copy size={18} />
                      </button>
                      <button
                        onClick={() => handleShowVersionHistory(page.page_id)}
                        className="p-1.5 hover:bg-muted rounded"
                        title="Version History"
                      >
                        <ClockCounterClockwise size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(page.page_id)}
                        className="p-1.5 hover:bg-red-100 text-red-600 rounded"
                        title="Delete"
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Enhanced Page Editor Modal */}
      {showEditor && (
        <PageEditorModal
          page={editingPage}
          onClose={() => setShowEditor(false)}
          onSave={handleSave}
        />
      )}

      {/* Version History Modal */}
      {showVersionHistory && (
        <VersionHistoryModal
          pageId={versionHistoryPageId}
          onClose={() => setShowVersionHistory(false)}
          onRestore={handleVersionRestore}
        />
      )}
    </div>
  );
}
